import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const ROLE_NAV_HIDDEN = {
  sales: ["stock", "operations", "web_sales", "data"],
  operations: ["data", "analytics", "marketing", "stock", "web_sales", "shop_sales"],
  support: ["data", "analytics", "marketing", "operations"],
  finance: ["data", "analytics", "marketing", "stock", "operations"],
  parts_loader: ["data", "analytics", "marketing", "stock", "operations", "web_sales", "shop_sales"],
  marketing: ["data"],
};

const OPS = ["operations"], STOCK = ["operations", "parts_loader"];
export const CARD_ROLES = {
  workload: ["sales", "operations", "support", "finance"],
  returns_week: ["operations", "parts_loader", "support"],
  expenses_month: ["finance"],
  lead_sources: ["sales", "marketing"],
  vehicle_alerts: STOCK, on_shift: OPS,
  attention: OPS, today_schedule: OPS, jobs_at_risk: OPS, engineer_status: OPS,
  ops_briefing: ["operations", "support", "parts_loader"],
  stock_alerts: STOCK, low_stock_reorder: STOCK, count_discrepancies: STOCK, slow_moving: STOCK, product_requests: STOCK,
  sales_snapshot: ["sales", "marketing", "finance"],
  unread_sms: ["sales", "support", "operations"],
  tile_jobs: ["sales", "operations", "support", "finance"],
  tile_shifts: OPS, tile_expenses: ["finance"], tile_vehicles: STOCK,
};

export const ALL_CARD_KEYS = ["ops_briefing", "quick_links", "returns_week", "my_todos", "my_notes", "weather", "workload", "expenses_month", "lead_sources", "assistant", "reports", "vehicle_alerts", "announcements", "on_shift", "attention", "today_schedule", "jobs_at_risk", "engineer_status", "stock_alerts", "sales_snapshot", "company_pulse", "trustpilot", "my_leave", "my_actions", "my_progress", "cover_gaps", "approvals", "training_quals", "attendance_overview", "unread_sms", "low_stock_reorder", "count_discrepancies", "slow_moving", "product_requests", "tile_jobs", "tile_shifts", "tile_expenses", "tile_vehicles"];

export function navHiddenFor(role, overrides) {
  if (overrides?.[role]) return overrides[role].nav_hidden || [];
  return ROLE_NAV_HIDDEN[role] || null;
}

export function cardHiddenFor(role, overrides) {
  if (overrides?.[role]) return overrides[role].card_hidden || [];
  if (!ROLE_NAV_HIDDEN[role]) return null;
  return Object.keys(CARD_ROLES).filter((k) => !CARD_ROLES[k].includes(role));
}

export const NAV_SECTION_PATHS = {
  stock: ["/stock", "/orders"],
  operations: ["/job-pool", "/scheduler", "/operations", "/live-tracking", "/load-board", "/runs", "/projects", "/reports/hotspots", "/forms"],
  web_sales: ["/web-sales"],
  shop_sales: ["/shop", "/epos/sales"],
  data: ["/admin", "/data", "/imports", "/settings", "/crews", "/databases", "/communications/settings"],
  analytics: ["/analytics", "/marketing/website-analytics", "/marketing/trends"],
  marketing: ["/media-library", "/reviews", "/communications/whatsapp", "/email-marketing"],
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [masking, setMasking] = useState(
    () => localStorage.getItem("htss_masking") === "true"
  );
  // Demo-only: preview the app as a different role type without re-authenticating.
  const [roles, setRoles] = useState([]);
  const [previewRoleKey, setPreviewRoleKey] = useState(
    () => localStorage.getItem("htss_preview_role") || null
  );

  const toggleMasking = useCallback(() => {
    setMasking((m) => {
      const next = !m;
      localStorage.setItem("htss_masking", String(next));
      return next;
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("htss_token");
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch((e) => {
        // A company remembered from a different account is not a reason to sign out: drop it and retry once.
        if (e?.response?.status === 403 && e?.config?.headers?.["X-Company-Id"]) {
          localStorage.removeItem("operix_company_id");
          return api.get("/auth/me").then((res) => setUser(res.data));
        }
        throw e;
      })
      .catch(() => {
        localStorage.removeItem("htss_token");
        setUser(false);
      });
  }, []);

  // Load the role catalogue for the "View as" demo switcher (privileged users only).
  useEffect(() => {
    const privileged =
      user && user !== true && (user.is_super || (user.permissions || []).includes("users.manage"));
    if (!privileged) {
      setRoles([]);
      return;
    }
    api
      .get("/admin/roles")
      .then((r) => setRoles(r.data.roles || []))
      .catch(() => setRoles([]));
  }, [user]);

  const [roleViews, setRoleViews] = useState({});
  const reloadRoleViews = useCallback(() => {
    api.get("/role-views").then((r) => setRoleViews(r.data.roles || {})).catch(() => {});
  }, []);
  useEffect(() => {
    if (user && user !== true) { reloadRoleViews(); }
  }, [user, reloadRoleViews]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password }, { headers: { "X-Client": "web" } });
    return data;
  }, []);

  const completeLogin = useCallback((data) => {
    localStorage.setItem("htss_token", data.access_token);
    if (data.user?.active_company_id) localStorage.setItem("operix_company_id", data.user.active_company_id);
    else localStorage.removeItem("operix_company_id");
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("htss_token");
    localStorage.removeItem("htss_preview_role");
    localStorage.removeItem("operix_company_id");
    setPreviewRoleKey(null);
    setUser(false);
  }, []);

  const updateUser = useCallback(
    (patch) => setUser((prev) => (prev && prev !== true ? { ...prev, ...patch } : prev)),
    []
  );

  const setPreviewRole = useCallback((key) => {
    setPreviewRoleKey(key);
    if (key) localStorage.setItem("htss_preview_role", key);
    else localStorage.removeItem("htss_preview_role");
  }, []);

  const previewRole = useMemo(
    () => (previewRoleKey ? roles.find((r) => r.key === previewRoleKey) || null : null),
    [previewRoleKey, roles]
  );

  // Effective identity used for all frontend gating. When previewing, we swap in
  // the chosen role's permissions/scope; the real token is still used for the API.
  const effective = useMemo(() => {
    if (!user || user === true || user === false) return null;
    if (previewRole) {
      const perms = previewRole.permissions === "*" ? [] : previewRole.permissions || [];
      return {
        is_super: previewRole.key === "super_admin" || previewRole.permissions === "*",
        permissions: perms,
        role: previewRole.key,
        role_name: previewRole.name,
      };
    }
    return {
      is_super: !!user.is_super,
      permissions: user.permissions || [],
      role: user.role,
      role_name: null,
    };
  }, [user, previewRole]);

  const can = useCallback(
    (perm) => !!effective && (effective.is_super || effective.permissions.includes(perm)),
    [effective]
  );
  const navHidden = useMemo(() => {
    if (!effective || effective.is_super) return new Set();
    const keys = previewRole ? [previewRole.key] : (user?.roles || []);
    if (!keys.length) return new Set();
    const sets = keys.map((k) => new Set(navHiddenFor(k, roleViews) || []));
    return new Set([...sets[0]].filter((s) => sets.every((x) => x.has(s))));
  }, [effective, previewRole, user, roleViews]);
  const cardAllowed = useCallback((key) => {
    if (!effective || effective.is_super) return true;
    const keys = previewRole ? [previewRole.key] : (user?.roles || []);
    if (!keys.length) return true;
    return !keys.every((k) => (cardHiddenFor(k, roleViews) || []).includes(key));
  }, [effective, previewRole, user, roleViews]);
  const canAny = useCallback(
    (perms) =>
      !!effective &&
      (effective.is_super || (perms || []).some((p) => effective.permissions.includes(p))),
    [effective]
  );

  // Phase 4: per-company module gating (frontend). Absent list => show (avoids
  // hiding anything for legacy tokens); server enforces the real entitlement.
  const enabledModules = user && user !== true ? user.active_enabled_modules : null;
  const moduleOn = useCallback(
    (m) => !Array.isArray(enabledModules) || enabledModules.includes(m),
    [enabledModules]
  );
  const isPlatformAdmin = !!(user && user !== true && user.platform_role === "operix_admin");
  const supportSession = user && user !== true ? user.support_session : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        completeLogin,
        logout,
        updateUser,
        masking,
        toggleMasking,
        can,
        canAny,
        moduleOn,
        isPlatformAdmin,
        supportSession,
        isSuper: !!effective?.is_super,
        navHidden,
        cardAllowed,
        roleViews,
        reloadRoleViews,
        roles,
        previewRoleKey,
        previewRole,
        setPreviewRole,
        previewing: !!previewRole,
        effectiveRole: effective?.role,
        effectiveRoleName: previewRole?.name,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
